import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowLeftOutlined, StopOutlined, EditOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { Box } from 'atoms';
import { getClientByIdV2, blacklistClientV2 } from 'store/clientSliceV2';
import {
	ClientBasicInfoFormV2,
	ClientBusinessInfoFormV2,
	ClientBlacklistModal,
	PageContainer,
	HeaderContainer,
	HeaderRightSection,
	Title,
	Subtitle,
	StepsContainer,
	CustomSteps,
	StepItem,
	StepCircle,
	StepLabel,
	StepConnector,
	BackButton,
	BlackListButton,
	EditButton,
	EMPTY_BILLING_ADDRESS,
	CLIENT_LIST_ROUTE,
} from 'componentsv2/forms/client';

export default function CreateClientPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	// Determine if we are in edit mode (id exists in URL)
	const isEditMode = Boolean(id);

	// View/Edit mode states
	const [isViewMode, setIsViewMode] = useState(isEditMode);
	const [isBlackListed, setIsBlackListed] = useState(false);
	const [showBlacklistModal, setShowBlacklistModal] = useState(false);
	const [blacklistLoading, setBlacklistLoading] = useState(false);

	// Step state
	const [currentStep, setCurrentStep] = useState(0);

	// Initialize data state (will be populated from API if in edit mode)
	const [basicInfoData, setBasicInfoData] = useState({});
	const [businessInfoData, setBusinessInfoData] = useState({
		billing_addresses: [EMPTY_BILLING_ADDRESS],
	});

	// Helper function to populate basic info from API result
	const populateBasicInfo = (result) => {
		// Extract address-related fields from head_office_address object if it exists
		const addressObj =
			typeof result.head_office_address === 'object' && result.head_office_address !== null
				? result.head_office_address
				: {};

		// Keep head_office_address as an object so the form can extract address_line1 from it
		// If it's already an object, use it; if it's a string, convert to object with address_line1
		let headOfficeAddressObj = {};
		if (typeof result.head_office_address === 'object' && result.head_office_address !== null) {
			headOfficeAddressObj = result.head_office_address;
		} else if (typeof result.head_office_address === 'string') {
			headOfficeAddressObj = { address_line1: result.head_office_address };
		}

		setBasicInfoData({
			client_legal_name: result.client_legal_name,
			company_name: result.company_name,
			legal_structure: result.legal_structure,
			primary_cost_center: result.primary_cost_center,
			primary_poc_name: result.primary_poc_name,
			primary_poc_contact: result.primary_poc_contact,
			finance_poc_name: result.finance_poc_name,
			finance_poc_contact: result.finance_poc_contact,
			email_id: result.email_id,
			// Keep head_office_address as object so form can extract address_line1
			head_office_address: headOfficeAddressObj,
			// Pincode, zone, city, state might be in the address object or at root level
			pincode: addressObj.pincode || result.pincode || '',
			zone: addressObj.zone || result.zone || '',
			city: addressObj.city || result.city || '',
			state: addressObj.state || result.state || '',
		});
	};

	// Helper function to populate business info from API result
	const populateBusinessInfo = (result) => {
		// Extract document keys from business_documents array
		const panDoc = result.business_documents?.find((doc) => doc.document_type === 'PAN');
		const gstDoc = result.business_documents?.find((doc) => doc.document_type === 'GST');

		// Extract additional documents (TAN, CIN, etc.) from business_documents
		const additionalDocs =
			result.business_documents
				?.filter(
					(doc) =>
						doc.document_type &&
						doc.document_type !== 'PAN' &&
						doc.document_type !== 'GST'
				)
				.map((doc) => ({
					document_type: doc.document_type,
					document_number: doc.document_number || doc.tan_number || doc.cin_number || '',
					document_file_key:
						doc.document_file_key || doc.tan_file_key || doc.cin_file_key || '',
					file_uploaded: !!(
						doc.document_file_key ||
						doc.tan_file_key ||
						doc.cin_file_key
					),
				})) || [];

		// Get GST type (use capitalized values as returned from API)
		const gstTypeValue = result.gst_type || gstDoc?.gst_type || '';
		const gstType = gstTypeValue || undefined;

		setBusinessInfoData({
			billing_addresses:
				result.billing_addresses?.length > 0
					? result.billing_addresses
					: [EMPTY_BILLING_ADDRESS],
			pan_number: panDoc?.pan_number || result.pan_number || '',
			gst_type: gstType,
			gst_number: gstDoc?.gst_number || result.gst_number || '',
			credit_period: result.credit_period || '',
			// Document keys for viewing
			pan_file_key: panDoc?.pan_file_key || '',
			gst_file_key: gstDoc?.gst_file_key || '',
			crf_file_key: result.crf_file_key || '',
			// Additional documents (TAN, CIN, etc.)
			additional_documents: additionalDocs,
		});
	};

	// Fetch client data if in edit mode
	useEffect(() => {
		if (!isEditMode || !id) return;

		const fetchClientData = async () => {
			try {
				const result = await dispatch(getClientByIdV2({ id }));
				if (result) {
					populateBasicInfo(result);
					populateBusinessInfo(result);
					if (result.is_blacklisted) {
						setIsBlackListed(true);
					}
				}
			} catch (error) {
				console.error('Error fetching client:', error);
			}
		};

		fetchClientData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isEditMode]);

	// Handle Edit button click
	const handleEditClick = () => {
		setIsViewMode(false);
	};

	// Handle Black List button click
	const handleBlackListClick = () => {
		if (isBlackListed) {
			// Already blacklisted, do nothing
			return;
		}
		setShowBlacklistModal(true);
	};

	// Handle Cancel button click
	const handleCancel = () => {
		if (isEditMode) {
			setIsViewMode(true);
		} else {
			navigate(CLIENT_LIST_ROUTE);
		}
	};

	// Handle blacklist confirm
	// eslint-disable-next-line no-unused-vars
	const handleBlacklistConfirm = async (reason) => {
		if (!id) {
			message.error('Client ID is required');
			return;
		}

		if (!isEditMode) {
			message.error('Cannot blacklist a client that is not yet created');
			return;
		}

		setBlacklistLoading(true);
		try {
			// Note: reason is collected but not sent in API payload currently
			// Can be added to API later if backend supports it
			const clientId = id;

			// Dispatch the blacklist action
			const result = await dispatch(
				blacklistClientV2({ id: clientId, is_blacklisted: true })
			);

			// Check if the action was successful
			// Redux Toolkit thunks return the result of the async function
			if (result?.status === 'success') {
				setIsBlackListed(true);
				setShowBlacklistModal(false);
				// Refresh client data to get updated status
				dispatch(getClientByIdV2({ id: clientId }));
			}
		} catch (error) {
			// Error is already handled in the slice function with toaster
		} finally {
			setBlacklistLoading(false);
		}
	};

	return (
		<PageContainer>
			{/* Header */}
			<HeaderContainer>
				<BackButton as={ArrowLeftOutlined} onClick={() => navigate(CLIENT_LIST_ROUTE)} />
				<Box>
					<Title>{isEditMode ? 'Client Information' : 'Create Client'}</Title>
					<Subtitle>
						{isEditMode ? 'Edit client information' : 'Add and onboard new clients'}
					</Subtitle>
				</Box>
				{isEditMode && (
					<HeaderRightSection>
						<BlackListButton onClick={handleBlackListClick} disabled={isBlackListed}>
							<StopOutlined />
							{isBlackListed ? 'Blacklisted' : 'Black List'}
						</BlackListButton>
						{isViewMode && (
							<EditButton onClick={handleEditClick}>
								<EditOutlined />
								Edit
							</EditButton>
						)}
					</HeaderRightSection>
				)}
			</HeaderContainer>

			{/* Steps */}
			<StepsContainer>
				<CustomSteps>
					<StepItem>
						<StepCircle active={currentStep === 0} completed={currentStep > 0}>
							{currentStep > 0 ? '✓' : '1'}
						</StepCircle>
						<StepLabel active={currentStep === 0}>Basic Information</StepLabel>
					</StepItem>
					<StepConnector completed={currentStep > 0} />
					<StepItem>
						<StepCircle active={currentStep === 1} completed={currentStep > 1}>
							2
						</StepCircle>
						<StepLabel active={currentStep === 1}>Business Information</StepLabel>
					</StepItem>
				</CustomSteps>
			</StepsContainer>

			{/* Step 1: Basic Information */}
			{currentStep === 0 && (
				<ClientBasicInfoFormV2
					basicInfoData={basicInfoData}
					setBasicInfoData={setBasicInfoData}
					setCurrentStep={setCurrentStep}
					currentStep={currentStep}
					isEditMode={isEditMode}
					isViewMode={isViewMode}
					onCancel={handleCancel}
				/>
			)}

			{/* Step 2: Business Information */}
			{currentStep === 1 && (
				<ClientBusinessInfoFormV2
					basicInfoData={basicInfoData}
					businessInfoData={businessInfoData}
					setBusinessInfoData={setBusinessInfoData}
					setCurrentStep={setCurrentStep}
					isEditMode={isEditMode}
					isViewMode={isViewMode}
					onCancel={handleCancel}
				/>
			)}

			{/* Blacklist Confirmation Modal */}
			<ClientBlacklistModal
				open={showBlacklistModal}
				onCancel={() => setShowBlacklistModal(false)}
				onConfirm={handleBlacklistConfirm}
				loading={blacklistLoading}
			/>
		</PageContainer>
	);
}
